import type { ExperimentMonitorStatsRow } from '@/state/experiments/useExperimentsMonitor';
import { NavLink } from '@/components/NavLink';
import { TrendingUp, TrendingDown, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';
import { formatMinorUnits } from 'helpers/formatters';
import { cn } from 'helpers/cn';
import { FOCUS_RING, MICRO_LABEL_SHAPE } from '../../ConsolePanel';
import { computeAggregatedMetrics, computeStatsByCurrency } from './utils';
import { ExpandedContent } from './ExpandedContent';

/**
 * The Trend column, in words.
 *
 * `flat` is a real reading, not a missing one: the middle band is a conversion rate
 * between 2.5% and 3.5%. An empty cell said nothing about which of the two it was.
 */
type Trend = 'positive' | 'negative' | 'flat';

const TREND_LABEL: Record<Trend, string> = { positive: 'Up', negative: 'Down', flat: 'Flat' };
const TREND_TONE: Record<Trend, string> = { positive: 'text-ok', negative: 'text-danger', flat: 'text-dim' };

/**
 * A row of the performance tables, as a grid rather than a `<tr>`.
 *
 * Head and body read their columns from `rowTemplate`, so the two cannot drift,
 * and `rowMinWidth` is what makes a narrow panel scroll the columns sideways
 * instead of folding a currency onto two lines.
 */

// First column is 32px, not 28px: the disclosure inside it is the only way to open a
// row, and at size-5 it was a 20px target — under WCAG 2.5.8's 24px floor and a
// coin-toss to tap. Widening the column is what lets the button grow with it.
export const rowTemplate = (showDomain: boolean) =>
  showDomain
    ? '32px minmax(180px,1fr) minmax(160px,1fr) 100px 110px 120px 72px'
    : '32px minmax(180px,1fr) 100px 110px 120px 72px';

export const rowMinWidth = (showDomain: boolean) => (showDomain ? 860 : 680);

type ExperimentRowHeadProps = { showDomain: boolean };

export const ExperimentRowHead = ({ showDomain }: ExperimentRowHeadProps) => (
  <div
    className="bg-panel2 border-line grid items-center gap-3 border-b px-3.5 py-2"
    style={{ gridTemplateColumns: rowTemplate(showDomain) }}
  >
    <span />
    <span className="micro-label truncate">Experiment</span>
    {showDomain && <span className="micro-label truncate">Domain</span>}
    <span className="micro-label text-right">Sessions</span>
    <span className="micro-label text-right">Conversions</span>
    <span className="micro-label text-right">Revenue</span>
    <span className="micro-label text-center">Trend</span>
  </div>
);

export type ExperimentRowProps = {
  experiment: ExperimentMonitorStatsRow;
  isExpanded: boolean;
  onToggle: () => void;
  showDomain: boolean;
  /** Set on one row only, for the product tour. */
  tourAnchor?: string;
};

export const ExperimentRow = ({ experiment, isExpanded, onToggle, showDomain, tourAnchor }: ExperimentRowProps) => {
  const { stats, variants } = experiment;

  const aggregated = computeAggregatedMetrics({ variants, stats });
  const statsByCurrency = computeStatsByCurrency({ stats, totalSessions: aggregated.totalSessions });
  const trend = aggregated.conversionRate > 3.5 ? 'positive' : aggregated.conversionRate < 2.5 ? 'negative' : null;

  // formatMinorUnits owns the division by 100 and degrades on an unrecognised code.
  // formatCurrency did neither: Intl throws RangeError on 'nill' and on 'EU4', both of
  // which are real values in currency_code, so the row threw rather than rendered.
  const formattedRevenue = formatMinorUnits(aggregated.totalRevenue, aggregated.primaryCurrency);

  return (
    <div className="border-line border-b last:border-b-0">
      <div
        className="hover:bg-hover grid items-center gap-3 px-3.5 py-2 transition-colors duration-150"
        style={{ gridTemplateColumns: rowTemplate(showDomain) }}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${experiment.experimentName}`}
          title={`${isExpanded ? 'Collapse' : 'Expand'} ${experiment.experimentName}`}
          aria-expanded={isExpanded}
          data-tour={tourAnchor}
          className={cn(
            'text-dim hover:bg-hover hover:text-foreground flex size-7 items-center justify-center rounded-lg',
            'transition-colors duration-150',
            FOCUS_RING,
          )}
        >
          {isExpanded ? (
            <ChevronDown className="size-4" strokeWidth={1.5} />
          ) : (
            <ChevronRight className="size-4" strokeWidth={1.5} />
          )}
        </button>

        <NavLink
          to={`/experiments/${experiment.experimentId}`}
          title={experiment.experimentName}
          className="flex min-w-0 items-center gap-1 text-meta hover:underline"
        >
          <span className="truncate">{experiment.experimentName}</span>
          <ExternalLink className="size-3 shrink-0" strokeWidth={1.5} />
        </NavLink>

        {showDomain && (
          <span className="supporting min-w-0 truncate" title={experiment.websiteUrl}>
            {experiment.websiteUrl}
          </span>
        )}

        <span className="num text-right">{aggregated.totalSessions.toLocaleString()}</span>
        <span className="num text-right">{aggregated.totalConversions.toLocaleString()}</span>
        <span className="num truncate text-right">{formattedRevenue}</span>
        {/*
          The arrow was the entire content of this cell: no text, no label, and
          nothing at all in the middle band, so "flat" was indistinguishable from
          "not computed yet". The word is what carries it now; the arrow and its
          colour reinforce it.
        */}
        <span className={cn('flex items-center justify-center gap-1', TREND_TONE[trend ?? 'flat'])}>
          {trend === 'positive' && <TrendingUp className="size-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />}
          {trend === 'negative' && <TrendingDown className="size-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />}
          <span className={MICRO_LABEL_SHAPE}>{TREND_LABEL[trend ?? 'flat']}</span>
        </span>
      </div>

      <ExpandedContent
        isExpanded={isExpanded}
        aggregated={aggregated}
        statsByCurrency={statsByCurrency}
        updatedAt={experiment.updatedAt}
      />
    </div>
  );
};
